import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp, getDoc } from "firebase/firestore";
import Select, { components } from "react-select";
import { Plus, X, CheckCircle, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getDepartments } from "../utils/getDepartments";
import { getEmployeesByDepartments } from "../utils/getEmployeesByDepartments";

function roleLabel(role) {
  const normalized = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (normalized === "super_admin") return "Super Admin";
  if (normalized === "admin") return "Admin";
  return "User";
}

export default function UserAdmin() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [directoryEmployees, setDirectoryEmployees] = useState([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showSelectedEmployeesModal, setShowSelectedEmployeesModal] = useState(false);
  const [selectedEmployeesModalSearch, setSelectedEmployeesModalSearch] = useState("");
  const [addSpecificEmail, setAddSpecificEmail] = useState("");
  const [addSpecificError, setAddSpecificError] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [updatingRoleId, setUpdatingRoleId] = useState(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setLoading(true);
      try {
        // 🔹 Fetch team members from Firebase teamMembers collection
        const teamMembersRef = collection(
          db,
          "workspaces",
          "ai-apps-dashboard",
          "teamMembers"
        );
        const teamMembersSnap = await getDocs(teamMembersRef);

        const members = teamMembersSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTeamMembers(members);
      } catch (err) {
        console.error("Error fetching team members:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  // 🔹 Fetch departments from BigQuery (directory_employees) for Add team member modal
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const result = await getDepartments();
        if (result.ok && Array.isArray(result.departments)) {
          setDepartments(result.departments);
        } else {
          setDepartments([]);
        }
      } catch (err) {
        console.error("Error fetching departments (BigQuery):", err);
        setDepartments([]);
      }
    };
    fetchDepts();
  }, []);

  // 🔹 When department(s) selected in Add modal, fetch employees from BigQuery by department
  // and default-select all addable employees (user can remove anyone they don't want)
  useEffect(() => {
    if (!showAddModal || !selectedDepartments || selectedDepartments.length === 0) {
      setDirectoryEmployees([]);
      setSelectedUsers([]);
      return;
    }
    const fetchByDepartment = async () => {
      setLoadingDirectory(true);
      try {
        const result = await getEmployeesByDepartments(selectedDepartments);
        if (result.ok && Array.isArray(result.employees)) {
          const employees = result.employees;
          setDirectoryEmployees(employees);
          // Default: select all employees who are not already team members (user can cross off any)
          const addable = employees.filter((emp) => {
            const email = (emp.email || "").toLowerCase().trim();
            if (!email) return false;
            return !teamMembers.some(
              (m) => (m.email || "").toLowerCase().trim() === email
            );
          });
          const options = addable.map((emp) => ({
            value: emp.email || emp.id,
            label: `${emp.fullName || emp.name || emp.email || "Unknown"} (${emp.email || ""})`,
            id: emp.id,
            email: emp.email,
            name: emp.fullName || emp.name,
            fullName: emp.fullName,
            department: emp.department,
            ...emp,
          }));
          setSelectedUsers(options);
        } else {
          setDirectoryEmployees([]);
          setSelectedUsers([]);
        }
      } catch (err) {
        console.error("Error fetching employees by department (BigQuery):", err);
        setDirectoryEmployees([]);
        setSelectedUsers([]);
      } finally {
        setLoadingDirectory(false);
      }
    };
    fetchByDepartment();
  }, [showAddModal, selectedDepartments]);

  // 🔹 Handle role change
  const handleRoleChange = async (id, newRole) => {
    setUpdatingRoleId(id);
    try {
      // Get member data from state (we already have it)
      const member = teamMembers.find((m) => m.id === id);
      if (!member) {
        console.error("Member not found in state:", id);
        setSuccessMessage(`Error: Team member not found.`);
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
        setUpdatingRoleId(null);
        return;
      }

      const memberData = member;
      let teamMemberUpdated = false;
      let userUpdated = false;

      // Try to update role in teamMembers collection (if document exists)
      try {
        const memberRef = doc(db, "workspaces", "ai-apps-dashboard", "teamMembers", id);
        const memberSnap = await getDoc(memberRef);
        
        if (memberSnap.exists()) {
          await updateDoc(memberRef, { 
            role: newRole,
            updatedAt: serverTimestamp()
          });
          teamMemberUpdated = true;
        } else {
          // TeamMember document doesn't exist with this ID, try to find by email
          if (memberData.email) {
            const teamMembersRef = collection(db, "workspaces", "ai-apps-dashboard", "teamMembers");
            const teamMembersSnap = await getDocs(teamMembersRef);
            const teamMemberDoc = teamMembersSnap.docs.find(
              (doc) => doc.data().email === memberData.email
            );
            if (teamMemberDoc) {
              const foundTeamMemberRef = doc(
                db,
                "workspaces",
                "ai-apps-dashboard",
                "teamMembers",
                teamMemberDoc.id
              );
              await updateDoc(foundTeamMemberRef, { 
                role: newRole,
                updatedAt: serverTimestamp()
              });
              teamMemberUpdated = true;
            }
          }
        }
      } catch (teamMemberErr) {
        console.error("Failed to update teamMember role:", teamMemberErr);
        // Continue - we'll still try to update the user
      }

      // Update role in users collection
      try {
        // Try to find user by ID first
        const userRef = doc(db, "workspaces", "ai-apps-dashboard", "users", id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          await updateDoc(userRef, { 
            role: newRole,
            updatedAt: serverTimestamp()
          });
          userUpdated = true;
        } else {
          // If not found by ID, try to find by email
          if (memberData.email) {
            const usersRef = collection(db, "workspaces", "ai-apps-dashboard", "users");
            const usersSnap = await getDocs(usersRef);
            const userDoc = usersSnap.docs.find(
              (doc) => doc.data().email === memberData.email
            );
            if (userDoc) {
              const foundUserRef = doc(
                db,
                "workspaces",
                "ai-apps-dashboard",
                "users",
                userDoc.id
              );
              await updateDoc(foundUserRef, { 
                role: newRole,
                updatedAt: serverTimestamp()
              });
              userUpdated = true;
            }
          }
        }
      } catch (userErr) {
        console.error("Failed to update user role in users collection:", userErr);
      }

      // If neither update succeeded, show error
      if (!teamMemberUpdated && !userUpdated) {
        setSuccessMessage(`Error: Could not update role. User not found in either collection.`);
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
        setUpdatingRoleId(null);
        return;
      }

      // Update local state immediately
      setTeamMembers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, role: newRole } : u))
      );

      // Show success message
      setSuccessMessage(`Role updated to ${newRole} successfully!`);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (err) {
      console.error("Failed to update role:", err);
      setSuccessMessage(`Failed to update role: ${err.message || "Unknown error"}`);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 5000);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  // 🔹 Handle adding team members (multiple)
  const handleAddTeamMember = async () => {
    if (!selectedUsers || selectedUsers.length === 0) return;

    setAddingMember(true);
    try {
      const teamMembersRef = collection(db, "workspaces", "ai-apps-dashboard", "teamMembers");
      const usersToAdd = [];
      const skippedUsers = [];

      // Check each selected user and prepare for adding (match by email; id may be from BigQuery)
      for (const selectedUser of selectedUsers) {
        const email = (selectedUser.email || "").toLowerCase().trim();
        const existingMember = teamMembers.find(
          (m) => (m.email || "").toLowerCase().trim() === email
        );
        
        if (existingMember) {
          skippedUsers.push(selectedUser.name || selectedUser.email || "Unknown");
        } else {
          usersToAdd.push(selectedUser);
        }
      }

      // Add all new users to Firebase teamMembers collection
      if (usersToAdd.length > 0) {
        const batchPromises = usersToAdd.map((user) =>
          addDoc(teamMembersRef, {
            email: user.email,
            name: user.name || user.fullName || user.email,
            ...(user.fullName != null && user.fullName !== "" ? { fullName: user.fullName } : {}),
            ...(user.department != null && user.department !== "" ? { department: user.department } : {}),
            role: user.role || "user",
            addedAt: serverTimestamp(),
          })
        );
        await Promise.all(batchPromises);
      }

      // Show feedback
      if (skippedUsers.length > 0 && usersToAdd.length > 0) {
        setSuccessMessage(
          `Added ${usersToAdd.length} team member(s). ${skippedUsers.length} user(s) were already team members and were skipped.`
        );
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
      } else if (skippedUsers.length > 0) {
        setSuccessMessage(`All selected users are already team members.`);
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
        setAddingMember(false);
        return;
      } else {
        setSuccessMessage(`Successfully added ${usersToAdd.length} team member(s)!`);
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 5000);
      }

      // Refresh team members list
      const teamMembersSnap = await getDocs(teamMembersRef);
      const members = teamMembersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeamMembers(members);

      // Reset form
      setSelectedUsers([]);
      setShowAddModal(false);
    } catch (err) {
      console.error("Failed to add team members:", err);
      setSuccessMessage("Failed to add team members. Please try again.");
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 5000);
    } finally {
      setAddingMember(false);
    }
  };

  // 🔹 Prepare options for "Add team member" from BigQuery directory_employees (exclude existing team members)
  const userOptions = directoryEmployees
    .filter((emp) => {
      const email = (emp.email || "").toLowerCase().trim();
      if (!email) return false;
      return !teamMembers.some(
        (member) => (member.email || "").toLowerCase().trim() === email
      );
    })
    .map((emp) => ({
      value: emp.email || emp.id,
      label: `${emp.fullName || emp.name || emp.email || "Unknown"} (${emp.email || ""})`,
      id: emp.id,
      email: emp.email,
      name: emp.fullName || emp.name,
      fullName: emp.fullName,
      department: emp.department,
      ...emp,
    }));

  // 🔹 Search filter
  const filteredMembers = teamMembers.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 🔹 UI
  if (loading)
    return (
      <div className="p-6 text-gray-600 dark:text-gray-300 bg-white dark:bg-[#0f172a] h-screen">
        Loading team members...
      </div>
    );

  return (
    <div className="p-6 bg-white dark:bg-[#0f172a] min-h-screen text-gray-900 dark:text-gray-100 transition-colors">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          Manage Team Members 
        </h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg 
          hover:bg-indigo-700 transition font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Team Member
        </button>
      </div>

      {/* 🔍 Search Box */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:w-96 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700
          bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100
          focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
        />
      </div>

      {/* 📋 Members Table */}
      <div className="overflow-x-auto rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm bg-white dark:bg-gray-900">
          <thead className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
            <tr>
              <th className="text-left p-3 border-b border-gray-300 dark:border-gray-700">
                Photo
              </th>
              <th className="text-left p-3 border-b border-gray-300 dark:border-gray-700">
                Name
              </th>
              <th className="text-left p-3 border-b border-gray-300 dark:border-gray-700">
                Email
              </th>
              <th className="text-left p-3 border-b border-gray-300 dark:border-gray-700">
                Role
              </th>
              <th className="text-left p-3 border-b border-gray-300 dark:border-gray-700">
                Change Role
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredMembers.map((user) => (
              <tr
                key={user.id}
                className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <td className="p-3">
                  <img
                    src={user.photo || "/default-avatar.png"}
                    alt={user.name || "User"}
                    className="w-10 h-10 rounded-full border border-gray-300 dark:border-gray-600 object-cover"
                  />
                </td>
                <td className="p-3">{user.name || "Unnamed"}</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">
                  {user.email || "—"}
                </td>
                <td className="p-3">{roleLabel(user.role)}</td>
                <td className="p-3">
                  {updatingRoleId === user.id ? (
                    <span className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                      Updating...
                    </span>
                  ) : (
                    <select
                      value={user.role || "user"}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={updatingRoleId === user.id}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                      bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 transition
                      disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}

            {filteredMembers.length === 0 && (
              <tr>
                <td
                  colSpan="5"
                  className="p-6 text-center text-gray-500 dark:text-gray-400"
                >
                  {teamMembers.length === 0
                    ? "No team members yet. Add from directory using the button above."
                    : "No matching team members found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Team Member Modal - constrained like AccessManagement (max-h, scrollable body) */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 dark:bg-black/60"
          onClick={() => {
            setShowAddModal(false);
            setSelectedUsers([]);
            setSelectedDepartments([]);
            setShowSelectedEmployeesModal(false);
            setAddSpecificEmail("");
            setAddSpecificError("");
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add Team Member
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedUsers([]);
                  setSelectedDepartments([]);
                  setShowSelectedEmployeesModal(false);
                  setAddSpecificEmail("");
                  setAddSpecificError("");
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left: Select Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Department
                  </label>
                  <Select
                    isMulti
                    options={departments.map((d) => ({ value: d, label: d }))}
                    value={(selectedDepartments || []).map((d) => ({ value: d, label: d }))}
                    onChange={(selected) =>
                      setSelectedDepartments(selected ? selected.map((s) => s.value) : [])
                    }
                    isSearchable
                    placeholder="Select department(s)..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#1f2937"
                          : "#ffffff",
                        borderColor: state.isFocused ? "#6366f1" : "#4b5563",
                        borderRadius: "0.5rem",
                        minHeight: "44px",
                        boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "",
                        "&:hover": { borderColor: "#6366f1" },
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: document.documentElement.classList.contains("dark")
                          ? "#111827"
                          : "#ffffff",
                        borderRadius: "0.5rem",
                        marginTop: "4px",
                        padding: "4px",
                        maxHeight: "280px",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused
                          ? (document.documentElement.classList.contains("dark") ? "#374151" : "#e0e7ff")
                          : "transparent",
                        color: state.isSelected ? "#ffffff" : (document.documentElement.classList.contains("dark") ? "#f9fafb" : "#111827"),
                        borderRadius: "0.375rem",
                        padding: "8px 12px",
                        cursor: "pointer",
                      }),
                      multiValue: (base) => ({ ...base, backgroundColor: "#6366f1", borderRadius: "0.375rem", padding: "0 4px" }),
                      multiValueLabel: (base) => ({ ...base, color: "white", fontSize: "0.875rem" }),
                      multiValueRemove: (base) => ({ ...base, color: "white", ":hover": { backgroundColor: "#4f46e5", color: "white" } }),
                      placeholder: (base) => ({ ...base, color: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280" }),
                      input: (base) => ({ ...base, color: document.documentElement.classList.contains("dark") ? "#ffffff" : "#111827" }),
                    }}
                  />
                </div>

                {/* Right: Select Employees (searchable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Employees (Searchable)
                  </label>
                  {selectedDepartments.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      Select department(s) to load employees.
                    </p>
                  ) : loadingDirectory ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      Loading employees...
                    </p>
                  ) : (
                    (() => {
                      const MAX_VISIBLE = 5;
                      // Include both department-selected (from userOptions) and specifically-added people
                      const allSelectedOptions = selectedUsers.map((su) => {
                        const email = (su.email || su.value || "").toLowerCase().trim();
                        const opt = userOptions.find((o) => (o.value || o.email || "").toLowerCase().trim() === email);
                        if (opt) return opt;
                        return {
                          value: su.value || su.email,
                          label: su.name ? `${su.name} (${su.email})` : (su.email || su.value),
                          email: su.email,
                          name: su.name,
                        };
                      });
                      const remainingCount = allSelectedOptions.length - MAX_VISIBLE;
                      const isDark = document.documentElement.classList.contains("dark");
                      return (
                        <div className="relative">
                          <Select
                            isMulti
                            options={userOptions}
                            value={allSelectedOptions}
                            onChange={(selected) => setSelectedUsers(selected ? selected : [])}
                            isClearable
                            isSearchable
                            placeholder="Search and select employees..."
                            className="basic-multi-select"
                            classNamePrefix="select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            components={{
                              MultiValue: (props) => {
                                const index = allSelectedOptions.findIndex((opt) => opt.value === props.data.value);
                                if (index >= MAX_VISIBLE) return null;
                                return <components.MultiValue {...props} />;
                              },
                            }}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                borderColor: state.isFocused ? "#6366f1" : (isDark ? "#4b5563" : "#d1d5db"),
                                borderRadius: "0.5rem",
                                minHeight: "44px",
                                boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
                                "&:hover": { borderColor: "#6366f1" },
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                borderRadius: "0.5rem",
                                marginTop: "4px",
                                padding: "4px",
                                maxHeight: "400px",
                                boxShadow: isDark ? "0 10px 15px -3px rgba(0,0,0,0.3)" : "0 10px 15px -3px rgba(0,0,0,0.1)",
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused
                                  ? (isDark ? "#374151" : "#e0e7ff")
                                  : "transparent",
                                color: state.isSelected ? "#ffffff" : (isDark ? "#f3f4f6" : "#111827"),
                                borderRadius: "0.375rem",
                                padding: "8px 12px",
                                cursor: "pointer",
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#6366f1",
                                borderRadius: "0.375rem",
                                padding: "0 4px",
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: "#ffffff",
                                fontSize: "0.875rem",
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: "#ffffff",
                                ":hover": { backgroundColor: "#4f46e5", color: "#ffffff" },
                              }),
                              placeholder: (base) => ({ ...base, color: isDark ? "#9ca3af" : "#6b7280" }),
                              input: (base) => ({ ...base, color: isDark ? "#f3f4f6" : "#111827" }),
                            }}
                          />
                          {remainingCount > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedEmployeesModalSearch("");
                                setShowSelectedEmployeesModal(true);
                              }}
                              className="mt-2 inline-flex items-center bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition cursor-pointer shadow-sm"
                            >
                              +{remainingCount} more employee{remainingCount !== 1 ? "s" : ""} selected — search & manage
                            </button>
                          )}
                          {selectedUsers.length > 0 && (
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                              {selectedUsers.length} employee(s) selected.
                            </p>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>

              {/* Add specific person by email (works even without selecting a department) */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add specific person
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    value={addSpecificEmail}
                    onChange={(e) => {
                      setAddSpecificEmail(e.target.value);
                      setAddSpecificError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const email = (addSpecificEmail || "").toLowerCase().trim();
                        if (!email) return;
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                          setAddSpecificError("Enter a valid email.");
                          return;
                        }
                        if (teamMembers.some((m) => (m.email || "").toLowerCase().trim() === email)) {
                          setAddSpecificError("Already a team member.");
                          return;
                        }
                        if (selectedUsers.some((u) => (u.email || u.value || "").toLowerCase().trim() === email)) {
                          setAddSpecificError("Already in selection.");
                          return;
                        }
                        setSelectedUsers((prev) => [...prev, { email, value: email, name: email, label: email }]);
                        setAddSpecificEmail("");
                        setAddSpecificError("");
                      }
                    }}
                    placeholder="Enter email to add (e.g. name@company.com)"
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const email = (addSpecificEmail || "").toLowerCase().trim();
                      if (!email) {
                        setAddSpecificError("Enter an email.");
                        return;
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        setAddSpecificError("Enter a valid email.");
                        return;
                      }
                      if (teamMembers.some((m) => (m.email || "").toLowerCase().trim() === email)) {
                        setAddSpecificError("Already a team member.");
                        return;
                      }
                      if (selectedUsers.some((u) => (u.email || u.value || "").toLowerCase().trim() === email)) {
                        setAddSpecificError("Already in selection.");
                        return;
                      }
                      setSelectedUsers((prev) => [...prev, { email, value: email, name: email, label: email }]);
                      setAddSpecificEmail("");
                      setAddSpecificError("");
                    }}
                    className="px-3 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition"
                  >
                    Add
                  </button>
                </div>
                {addSpecificError && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{addSpecificError}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Add someone by email even if they are not in the department list above.
                </p>
              </div>

              {selectedDepartments.length > 0 && !loadingDirectory && userOptions.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  {directoryEmployees.length === 0
                    ? "No employees in selected department(s) or directory could not be loaded."
                    : "All employees in selected department(s) are already team members."}
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedUsers([]);
                  setSelectedDepartments([]);
                  setShowSelectedEmployeesModal(false);
                  setAddSpecificEmail("");
                  setAddSpecificError("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTeamMember}
                disabled={!selectedUsers || selectedUsers.length === 0 || addingMember}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white 
                hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {addingMember ? "Adding..." : `Add ${selectedUsers.length > 0 ? `${selectedUsers.length} ` : ""}Member${selectedUsers.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected employees — search & manage modal (same as AccessManagement) */}
      <AnimatePresence>
        {showAddModal && showSelectedEmployeesModal && (() => {
          const allSelectedOptionsForModal = selectedUsers.map((su) => {
            const email = (su.email || su.value || "").toLowerCase().trim();
            const opt = userOptions.find((o) => (o.value || o.email || "").toLowerCase().trim() === email);
            if (opt) return opt;
            return {
              value: su.value || su.email,
              label: su.name ? `${su.name} (${su.email})` : (su.email || su.value),
              email: su.email,
              name: su.name,
            };
          });
          return (
            <div
              className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 dark:bg-black/60"
              onClick={() => setShowSelectedEmployeesModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Selected employees ({allSelectedOptionsForModal.length})
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowSelectedEmployeesModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="px-5 pt-4 pb-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value={selectedEmployeesModalSearch}
                      onChange={(e) => setSelectedEmployeesModalSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0">
                  {(() => {
                    const q = (selectedEmployeesModalSearch || "").toLowerCase().trim();
                    const filtered = q
                      ? allSelectedOptionsForModal.filter(
                          (opt) =>
                            (opt.label || "").toLowerCase().includes(q) ||
                            (opt.email || "").toLowerCase().includes(q)
                        )
                      : allSelectedOptionsForModal;
                    if (filtered.length === 0) {
                      return (
                        <p className="py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                          {q ? "No selected employees match your search." : "No employees selected."}
                        </p>
                      );
                    }
                    return (
                      <ul className="space-y-1.5 py-2">
                        {filtered.map((opt) => (
                          <li
                            key={opt.value}
                            className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                          >
                            <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                              {opt.label}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedUsers((prev) =>
                                  prev.filter((u) => (u.email || u.value) !== opt.value)
                                )
                              }
                              className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                              title="Remove from selection"
                            >
                              <X size={16} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
                <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowSelectedEmployeesModal(false)}
                    className="px-4 py-2 rounded-xl bg-indigo-600 dark:bg-indigo-500 text-white font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

      {/* Success/Error Popup */}
      <AnimatePresence>
        {showSuccessPopup && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className="fixed top-4 right-4 z-50 max-w-md"
          >
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl border p-4 flex items-start gap-3 ${
              successMessage.includes("Error") || successMessage.includes("Failed")
                ? "border-red-200 dark:border-red-800"
                : "border-green-200 dark:border-green-800"
            }`}>
              <div className="flex-shrink-0">
                {successMessage.includes("Error") || successMessage.includes("Failed") ? (
                  <X className="w-6 h-6 text-red-500" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  successMessage.includes("Error") || successMessage.includes("Failed")
                    ? "text-red-900 dark:text-red-100"
                    : "text-gray-900 dark:text-gray-100"
                }`}>
                  {successMessage}
                </p>
              </div>
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}













